import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDeleteDocScan, apiUploadDocScan, docScanUrl, type DocScanKind, type DocScanTarget } from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';

/**
 * Identity-document scan field (passport / visa) — 2026-10-07.
 *
 * Used by BOTH the team card editor (TeamEditPage → /api/team/:id/documents/…)
 * and the self-service card (AccountPage → /api/me/team/documents/…), so the
 * admin and the musician get exactly the same affordance (project rule).
 *
 * The image/PDF is stored ENCRYPTED on the server and served by a guarded
 * route, so the thumbnail is a same-origin `<img>` on that route (the session
 * cookie rides along) — never a public URL. A caller who may not read the scan
 * sees no reference at all (`scanPath` arrives empty from the API).
 *
 * `buildScanDataUrl` is EXPORTED so the direct empty-state upload flow
 * (TeamEditPage: create passport + upload scan + auto-fill MRZ in one step)
 * reuses EXACTLY the same HEIC re-encoding / PDF rules as this field.
 */
interface Props {
  kind: DocScanKind;
  docId: string;
  /** Owner/member id for the team route; ignored when `self` is true. */
  memberId?: string;
  /** Use the self-service route (the caller's OWN document). */
  self?: boolean;
  /** Server-side reference (`<memberId>/<uuid>.<ext>`) or '' when absent/hidden. */
  scanPath?: string;
  /** A scan EXISTS but this caller may not read it (reference withheld). */
  hasScan?: boolean;
  /** May upload / remove the scan. */
  canEdit?: boolean;
  onChanged?: (scanPath: string) => void;
  /** Render the label row (default true). */
  showLabel?: boolean;
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_INLINE_BYTES = 6 * 1024 * 1024; // above this, images are re-encoded
const MAX_PDF_BYTES = 15 * 1024 * 1024;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Re-encode ANY decodable image to JPEG (max 2400 px) through a canvas.
 * WHY: iPhones save HEIC by default and the server accepts only PNG/JPEG/WebP/
 * PDF — without this, every phone photo would fail with a 400. It also keeps a
 * 12 MP photo well under the upload cap. A browser that cannot decode the file
 * (Chrome on HEIC) rejects with `unsupported-image` so the user is told.
 */
function reencodeToJpeg(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const max = 2400;
        const scale = Math.min(1, max / Math.max(img.width || max, img.height || max));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round((img.width || max) * scale));
        canvas.height = Math.max(1, Math.round((img.height || max) * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('unsupported-image'));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (e) {
        reject(new Error('unsupported-image'));
      }
    };
    img.onerror = () => reject(new Error('unsupported-image'));
    img.src = dataUrl;
  });
}

/**
 * Shared scan-encoding pipeline (used by DocScanField AND by the direct
 * empty-state upload in TeamEditPage): PDFs pass through (≤ 15 MB), any other
 * image is re-encoded to JPEG ≤ 2400 px unless it is already a small
 * PNG/JPEG/WebP. Throws 'too-large' / 'unsupported-image' like onFile did.
 */
export async function buildScanDataUrl(file: File): Promise<string> {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (isPdf) {
    if (file.size > MAX_PDF_BYTES) throw new Error('too-large');
    return readAsDataUrl(file);
  }
  const raw = await readAsDataUrl(file);
  return IMAGE_TYPES.includes(file.type) && file.size <= MAX_INLINE_BYTES
    ? raw
    : reencodeToJpeg(raw);
}

export default function DocScanField({
  kind, docId, memberId, self, scanPath, hasScan = false, canEdit = false, onChanged, showLabel = true,
}: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const target: DocScanTarget = { memberId, self };
  const url = scanPath ? docScanUrl(kind, docId, target) : '';
  const isPdf = /\.pdf$/i.test(scanPath || '');

  const pick = () => { setError(null); inputRef.current?.click(); };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const payload: { dataUrl: string } = { dataUrl: await buildScanDataUrl(file) };
      const res = await apiUploadDocScan(kind, docId, payload, target);
      onChanged?.(res.scan_path || '');
      reportSaveOk('team');
    } catch (e: any) {
      const msg = e?.message === 'unsupported-image' ? t('docs.convertFailed')
        : e?.message === 'Could not read the file' ? t('docs.readFailed')
          : e?.message === 'too-large' ? t('docs.tooLarge')
            : e?.message || t('docs.uploadFailed');
      setError(msg);
      reportSaveError('team', e);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiDeleteDocScan(kind, docId, target);
      onChanged?.('');
      reportSaveOk('team');
    } catch (e: any) {
      setError(e?.message || t('docs.deleteFailed'));
      reportSaveError('team', e);
    } finally {
      setBusy(false);
    }
  };

  const btnStyle = { padding: '4px 10px', fontSize: '0.8rem' };

  return (
    <div style={{ marginTop: 8 }}>
      {showLabel && (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: 4 }}>{t('docs.scan')}</div>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {scanPath && (isPdf ? (
          <a className="btn btn-small" style={{ ...btnStyle, textDecoration: 'none' }}
            href={url} target="_blank" rel="noreferrer">{t('docs.openPdf')}</a>
        ) : (
          <a href={url} target="_blank" rel="noreferrer" title={t('docs.openFull')}>
            <img src={url} alt={t('docs.scan')}
              style={{ maxWidth: 160, maxHeight: 110, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)', display: 'block' }} />
          </a>
        ))}
        {!scanPath && hasScan && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>{t('docs.scanRestricted')}</span>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {!scanPath && !hasScan && <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{t('docs.noScan')}</span>}
          {canEdit && (
            <button type="button" className="btn btn-small" style={btnStyle} onClick={pick} disabled={busy}>
              {busy ? t('docs.uploading') : scanPath ? t('docs.replace') : t('docs.addScan')}
            </button>
          )}
          {canEdit && scanPath && (
            <button type="button" className="btn btn-small btn-danger" style={btnStyle} onClick={remove} disabled={busy}>
              {t('docs.removeScan')}
            </button>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => void onFile(e.target.files?.[0])} />
      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: 4 }}>{t('docs.hint')}</div>
      {error && <div style={{ fontSize: '0.8rem', color: 'var(--danger)', marginTop: 4 }}>{error}</div>}
    </div>
  );
}